import { Icons } from '@eppendorf/vnls-css/dist';
import {
  IncidentStatusEnum,
  type IncidentStatus,
} from '@eppendorf/vnls-notifications-utils';
import { Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import './incident-detail-card-header-content.scss';

const config: Record<
  IncidentStatusEnum | string,
  {
    icon: Icons;
    iconBg: string;
    iconSize?: IconSizeClasses;
    iconColor?: string;
    borderColor: string;
    text:
      | 'incidents.recipients'
      | 'incidents.initialRecipients'
      | 'incidents.incidentsStatus.escalated_1'
      | 'incidents.incidentsStatus.escalated_2'
      | 'incidents.incidentsStatus.expired'
      | 'incidents.incidentsStatus.acknowledged'
      | 'incidents.incidentsStatus.resolved'
      | 'incidents.activeDelay'
      | 'incidents.inActiveDelay';
    textColor: string;
  }
> = {
  [IncidentStatusEnum.NoActionRequired]: {
    icon: 'escalation',
    iconBg: 'bg-blue-500',
    borderColor: 'border-color-blue-500',
    text: 'incidents.recipients',
    textColor: 'font-weight-bold',
  },
  [IncidentStatusEnum.PendingSendNotification]: {
    icon: 'delay',
    iconSize: IconSizeClasses.Small,
    iconBg: 'bg-blue-800',
    iconColor: 'bg-white',
    borderColor: '',
    text: 'incidents.activeDelay',
    textColor: 'font-weight-bold',
  },
  PendingSendNotificationInactive: {
    icon: 'delay',
    iconSize: IconSizeClasses.Small,
    iconBg: 'bg-grey-900',
    borderColor: '',
    text: 'incidents.inActiveDelay',
    textColor: 'font-weight-bold',
  },
  [IncidentStatusEnum.ActionRequired]: {
    icon: 'escalation',
    iconBg: 'bg-blue-500',
    borderColor: 'border-color-blue-500',
    text: 'incidents.initialRecipients',
    textColor: 'font-weight-bold',
  },
  [IncidentStatusEnum.Escalated_1]: {
    icon: 'escalation',
    iconBg: 'bg-orange-500',
    borderColor: 'border-color-orange-500',
    text: 'incidents.incidentsStatus.escalated_1',
    textColor: 'font-weight-bold',
  },
  [IncidentStatusEnum.Escalated_2]: {
    icon: 'escalation',
    iconBg: 'bg-red-500',
    borderColor: 'border-color-red-500',
    text: 'incidents.incidentsStatus.escalated_2',
    textColor: 'font-weight-bold',
  },
  [IncidentStatusEnum.Expired]: {
    icon: 'alarm-off',
    iconBg: 'bg-red-500',
    borderColor: 'border-color-red-500',
    text: 'incidents.incidentsStatus.expired',
    textColor: 'font-weight-bold color-red-500',
  },
  [IncidentStatusEnum.Acknowledged]: {
    icon: 'success',
    iconBg: 'incident-detail-card-header--success-icon',
    borderColor: '',
    text: 'incidents.incidentsStatus.acknowledged',
    textColor: 'font-weight-bold',
  },
  [IncidentStatusEnum.Resolved]: {
    icon: 'success',
    iconBg: 'incident-detail-card-header--success-icon',
    borderColor: '',
    text: 'incidents.incidentsStatus.resolved',
    textColor: 'font-weight-bold',
  },
};

export type IncidentDetailCardHeaderContentProps = {
  type: IncidentStatus | string;
};

export function IncidentDetailCardHeaderContent({
  type,
}: IncidentDetailCardHeaderContentProps) {
  const { t } = useTranslation();
  const isFinalState =
    type === IncidentStatusEnum.Acknowledged || type === IncidentStatusEnum.Resolved;
  const { icon, iconBg, iconSize, iconColor, text, textColor, borderColor } =
    config[type as keyof typeof config];

  return (
    <div className="flex flex__ai--center gap-m">
      <div
        className={cn(
          'icon--s flex flex__ai--center flex__jc--center',
          iconBg,
          borderColor,
          {
            'bg-white border-solid border-s border-radius-xxl':
              !isFinalState && borderColor,
          },
        )}
      >
        <Icon
          name={icon}
          size={
            iconSize || (isFinalState ? IconSizeClasses.Small : IconSizeClasses.XSmall)
          }
          className={cn(iconBg, iconColor)}
        />
      </div>
      <div className={textColor}>{t(text)}</div>
    </div>
  );
}
